import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';
import { Mail, Lock, Eye, EyeOff, Building2, User, Phone, MapPin, Globe, UserPlus, AlertCircle, CheckCircle, Key, ArrowRight, ArrowLeft } from 'lucide-react';
import PublicFooter from '../components/PublicFooter';

const Register = () => {
  const [step, setStep] = useState(1); // 1 = invite code, 2 = registration form
  const [inviteCode, setInviteCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    contactName: '',
    phone: '',
    address: '',
    website: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setVerifyingCode(true);

    try {
      await api.post('/auth/verify-invite', { code: inviteCode.toUpperCase().trim() });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid invite code');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const passwordRequirements = [
    { met: formData.password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(formData.password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(formData.password), text: 'One number' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await register({ ...formData, inviteCode: inviteCode.toUpperCase().trim() });
      navigate('/branding?onboarding=1', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 flex items-center justify-center p-4 py-12">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-500/20 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-lg mb-4 p-3">
            <img src="/ttic-logo.png" alt="TTIC Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white font-display">
            Create your account
          </h1>
          <p className="text-white/80 mt-2">
            {step === 1 ? 'First, enter the invite code your TTIC Agency Rep gave you.' : 'Your code is confirmed. Add your details to get started.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2 mb-4 animate-fade-in">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {step === 1 ? (
            // STEP 1: Invite Code Entry
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">Step 1 of 2</p>
                <h2 className="mt-1 text-xl font-bold text-gray-900">Enter your invite code</h2>
                <p className="mt-2 text-sm text-gray-600">This code gives you access to the Hub. After we confirm it, you'll create your login and set up your brand.</p>
              </div>
              <div>
                <label htmlFor="register-invite-code" className="block text-sm font-medium text-gray-700 mb-2">
                  Invite Code
                </label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    id="register-invite-code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    className="input-field pl-11 uppercase font-mono tracking-wider text-lg"
                    placeholder="TTIC-XXXXXX"
                    required
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    maxLength={50}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Your code looks like TTIC-XXXXXX.
                </p>
              </div>

              <button
                type="submit"
                disabled={verifyingCode || !inviteCode}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {verifyingCode ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    Confirm code & continue <ArrowRight size={18} />
                  </>
                )}
              </button>

              <div className="rounded-xl bg-primary-50 p-4">
                <p className="text-sm text-gray-700">Don't have a code, or has yours expired?</p>
                <Link to="/request-invite" className="mt-1 inline-flex min-h-11 items-center gap-2 font-semibold text-primary-600 hover:text-primary-700">
                  Request an invite code <ArrowRight size={16} />
                </Link>
              </div>
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary-500 hover:text-primary-600 font-medium">
                  ← Back to Login
                </Link>
              </div>
            </form>
          ) : (
            // STEP 2: Registration Form
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">Step 2 of 2 · Your account details</p>
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={18} />
                <span className="text-sm text-green-800">Invite code verified: <strong>{inviteCode}</strong></span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} className="input-field pl-11" placeholder="Your Title Company" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className="input-field pl-11" placeholder="John Smith" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="input-field pl-11" placeholder="you@company.com" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-field pl-11" placeholder="(555) 123-4567" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="text" name="address" value={formData.address} onChange={handleChange} className="input-field pl-11" placeholder="123 Main St, City, ST 12345" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="text" name="website" value={formData.website} onChange={handleChange} className="input-field pl-11" placeholder="www.yourcompany.com" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} className="input-field pl-11 pr-11" placeholder="••••••••" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  {passwordRequirements.map((req, idx) => (
                    <div key={idx} className={`flex items-center gap-2 text-xs ${req.met ? 'text-green-600' : 'text-gray-400'}`}>
                      <CheckCircle size={12} className={req.met ? 'opacity-100' : 'opacity-30'} />
                      {req.text}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type={showPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="input-field pl-11" placeholder="••••••••" required />
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setStep(1)} className="btn-outline flex-shrink-0 flex items-center justify-center gap-2 px-4">
                  <ArrowLeft size={18} />
                </button>
                <button type="submit" disabled={loading} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <UserPlus size={18} />
                      Create Account
                    </>
                  )}
                </button>
              </div>
              <p className="text-center text-xs leading-5 text-gray-500">
                By creating an account, you agree to the <Link to="/terms" className="font-semibold text-primary-600 hover:underline">Terms of Use</Link> and acknowledge the <Link to="/privacy" className="font-semibold text-primary-600 hover:underline">Privacy Policy</Link>.
              </p>
            </form>
          )}

          {step === 2 && (
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-500 hover:text-primary-600 font-semibold">
                  Sign In
                </Link>
              </p>
            </div>
          )}
        </div>
        <PublicFooter dark compact />
      </div>
    </div>
  );
};

export default Register;
